import { useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import Image from 'next/legacy/image'
import PropTypes from 'prop-types'
import debounce from 'debounce'

import {
  SEO,
  Section,
  Text,
  TextInput,
  Loading,
  PageSelector,
} from '@components'
import CommonPageLayout from '@layouts/CommonPageLayout'

import ArticleList from './sections/ArticleList'
import FeaturedArticle from './sections/FeaturedArticle'
import PopularArticles from './sections/PopularArticles'

import styles from './style.module.css'

import { getArticles, getArticleCategories } from '@gql/blogQuery'

function BlogHomeModule({ PAGE_SIZE, POPULAR_ARTICLE_LIMIT }) {
  const router = useRouter()
  const { data: { categories = [] } = {} } = useQuery(getArticleCategories)
  const {
    loading,
    data: { results = {} } = {},
    refetch: refetchArticles,
  } = useQuery(getArticles, {
    variables: {
      pageSize: PAGE_SIZE,
      page: router.query.page ? Number(router.query.page) : 1,
      category: router.query.category || null,
      subcategory: router.query.subcategory || null,
      keyword: router.query.search || null,
      tags: router.query.tags || null,
    },
  })

  const onChangeSearchUrl = debounce(value => {
    if (value === '') {
      router.push(`/blog`, undefined, { shallow: true })
    } else {
      const newParams = new URLSearchParams({ search: value })
      router.push(`/blog?${newParams.toString()}`, '/blog', { shallow: true })
    }
  }, 1000)

  const onChangeCategory = categoryIndex => {
    const categoryName = categories[categoryIndex].name
    if (router.query.subcategory) router.query.subcategory = undefined
    const newParams = new URLSearchParams({ category: categoryName })
    router.push(`/blog?${newParams.toString()}`, '/blog', { shallow: true })
  }

  useEffect(() => {
    if (router.query) {
      refetchArticles({
        page: router.query.page ? Number(router.query.page) : 1,
        category: router.query.category || null,
        subcategory: router.query.subcategory || null,
        keyword: router.query.search || null,
        tags: router.query.tags || null,
      })
    }
  }, [router.query, refetchArticles])

  const categoryName =
    router.query.category || categories[0] ? categories[0].name : ''
  const firstArticlesChunk = results.articles
    ? results.articles.slice(0, 2)
    : []
  const secondArticlesChunk = results.articles ? results.articles.slice(2) : []

  return (
    <>
      <SEO
        title="BASE, Your Personalized Skincare Blog"
        description="Easy read about skincare, hormones & self-love from BASE experts"
      />

      <div className={styles.searchContainer}>
        <div className={styles.leftColumn} />
        <div className={styles.rightColumn}>
          <TextInput
            type="text"
            placeholder="Search blog..."
            value={undefined}
            onChange={e => onChangeSearchUrl(e.target.value)}
            inputIcon="search"
            showClearButton
            onClickClearButton={() => onChangeSearchUrl('')}
            containerClassName={`
              ${styles.searchInput}
              ${router.query.search && styles.maxWidth}
            `}
          />
        </div>
      </div>

      <Section className={styles.firstSection}>
        {/* Category Section */}
        <div className={styles.leftColumn}>
          <Text tag="h1" variant="subtitle">
            Our Blog
          </Text>

          <div className={styles.categoryList}>
            {categories.map(({ id, name }, index) => {
              const isSelected = router.query.category
                ? router.query.category === name
                : index === 0

              return (
                <div
                  key={id}
                  onClick={() => onChangeCategory(index)}
                  className={styles.categoryItem}
                >
                  <Text
                    tag="h2"
                    variant="lightTitle"
                    color={isSelected ? 'black' : 'gray'}
                  >
                    {name}
                    &nbsp;
                    {isSelected && (
                      <span className={styles.rightArrow}>
                        <Image
                          alt="selected category"
                          src="https://static.base.co.id/scroll-right-black.svg"
                          height={23}
                          width={45}
                        />
                      </span>
                    )}
                  </Text>
                </div>
              )
            })}
          </div>
        </div>

        {/* Featured Article Section or Article List on search */}
        <div className={styles.rightColumn}>
          {router.query.search ? (
            <>
              {loading ? (
                <Loading />
              ) : (
                <Text tag="h4" variant="lightTitle" className={styles.pageText}>
                  Page {results.meta.page || 0} of {results.meta.pageTotal || 0}{' '}
                  results
                </Text>
              )}
              <ArticleList
                articles={firstArticlesChunk}
                searchQuery={router.query.search}
              />
            </>
          ) : (
            <FeaturedArticle category={categoryName} />
          )}
        </div>
      </Section>

      <Section className={styles.secondSection}>
        {/* Popular Article section */}
        <div className={styles.leftColumn}>
          <Text tag="h3" variant="articleTitle">
            Popular Articles &nbsp;
            <span className={styles.popularArticleIcon}>
              <Image
                alt="popular article"
                src="https://static.base.co.id/skin-type-normal_.svg"
                height={48}
                width={28}
              />
            </span>
          </Text>
          <PopularArticles limit={POPULAR_ARTICLE_LIMIT} />
        </div>

        {/* Article List section */}
        <div className={styles.rightColumn}>
          {loading ? (
            <Loading />
          ) : (
            <>
              <ArticleList
                articles={
                  router.query.search ? secondArticlesChunk : results.articles
                }
                searchQuery={router.query.search}
              />
              <PageSelector
                page={results.meta.page}
                pageTotal={results.meta.pageTotal}
                shallow
              />
            </>
          )}
        </div>
      </Section>
    </>
  )
}

BlogHomeModule.propTypes = {
  PAGE_SIZE: PropTypes.number.isRequired,
  POPULAR_ARTICLE_LIMIT: PropTypes.number.isRequired,
}

BlogHomeModule.getLayout = page => {
  return (
    <CommonPageLayout containerClassName={styles.container} withWidget={false}>
      {page}
    </CommonPageLayout>
  )
}

export default BlogHomeModule
